import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { HTTPError } from 'ky';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { apiClient } from '@/shared/api/client';
import { useSessionStore } from '@/entities/session';
import { CONSENTS_PATH, needsLegalConsentsFromProfile } from '@/shared/lib/legal/docs';

export function ConsentGuard() {
  const location = useLocation();
  const logout = useSessionStore((s) => s.logout);
  const [loading, setLoading] = useState(true);
  const [needsConsents, setNeedsConsents] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await apiClient.get('profile/me').json<{
          pdnAccepted?: boolean;
          cookiesAccepted?: boolean;
        }>();
        if (!cancelled) setNeedsConsents(needsLegalConsentsFromProfile(p));
      } catch (e) {
        if (cancelled) return;
        if (e instanceof HTTPError && e.response.status === 401) {
          logout();
          return;
        }
        setNeedsConsents(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, logout]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <PageSpinner />
      </div>
    );
  }

  if (needsConsents && location.pathname !== CONSENTS_PATH) {
    return <Navigate to={CONSENTS_PATH} replace state={{ from: location }} />;
  }

  if (!needsConsents && location.pathname === CONSENTS_PATH) {
    return <Navigate to="/app" replace />;
  }

  return <Outlet />;
}
